import { AggregationFunction } from "@multiweb/clients/dataReader";
import { IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { IItemRightsCollection } from "../../../../Global/Base/Rights/RightTypes";
import { ChartType } from "../../ChartElement";
import { BooleanColumn } from "./BooleanColumn";
import { ColorColumn } from "./ColorColumn";
import { Column } from "./Column";
import { DateColumn } from "./DateColumn";
import { DateTimeColumn } from "./DateTimeColumn";
import { EnumColumn } from "./EnumColumn";
import { FileColumn } from "./FileColumn";
import { FlagColumn } from "./FlagColumn";
import { HTMLTextColumn } from "./HTMLTextColumn";
import { IconColumn } from "./IconColumn";
import { LovColumn } from "./LOVColumn";
import { NumberColumn } from "./NumberColumn";
import { TimeColumn } from "./TimeColumn";
import { TimeSpanColumn } from "./TimeSpanColumn";
/**
 * @includeToDoc
 */
export class ColumnFactory {
    private static _columns: CaseInsensitiveMap<typeof Column> = new CaseInsensitiveMap<typeof Column>();
    private static _initialized = false;

    /**
     * "Staticky konstruktor" pro tridu
     */
    public static init(): void {
        if (ColumnFactory._initialized)
            return;
        ColumnFactory._initialized = true;

        ColumnFactory.add("FileEditAttribute", FileColumn);
        ColumnFactory.add("LOVAttribute", LovColumn);

        // Podporované datové typy https://wiki-dokumentace/index.php?title=Podporovan%C3%A9_datov%C3%A9_typy

        ColumnFactory.add(OWDataType.date, DateColumn);
        ColumnFactory.add(OWDataType.dateTime, DateTimeColumn);
        ColumnFactory.add(OWDataType.time, TimeColumn);
        ColumnFactory.add(OWDataType.timeSpan, TimeSpanColumn);

        ColumnFactory.add(OWDataType.boolean, BooleanColumn);
        ColumnFactory.add(OWDataType.enum, EnumColumn);
        ColumnFactory.add(OWDataType.flags, FlagColumn);
        ColumnFactory.add(OWDataType.document, FileColumn);
        ColumnFactory.add(OWDataType.color, ColorColumn);

        ColumnFactory.add(OWDataType.decimal, NumberColumn);
        ColumnFactory.add(OWDataType.money, NumberColumn);

        ColumnFactory.add(OWDataType.bcd, NumberColumn);
        ColumnFactory.add(OWDataType.double, NumberColumn);
        ColumnFactory.add(OWDataType.single, NumberColumn);

        ColumnFactory.add(OWDataType.integer, NumberColumn);
        ColumnFactory.add(OWDataType.int64, NumberColumn);
        ColumnFactory.add(OWDataType.short, NumberColumn);
        ColumnFactory.add(OWDataType.byte, NumberColumn);
        ColumnFactory.add(OWDataType.icon, IconColumn);

        ColumnFactory.add(OWDataType.htmlText, HTMLTextColumn);

        /*
        http://sharepoint/vyroba/Dokumentace/Ovládací%20prvky%20-%20seznam.aspx
        Attribute
        CalendarItem
        FileEditAttribute
        ChartFrameAttribute
        LinkAttribute
        LOVAttribute
        TextLOVAttribute
        TreeAttribute
        TwoListFrameAttribute
        NumAttribute
        ProgressAttribute
        */
    }

    public static add(columnName: string, controlClass: typeof Column): void {
        if (!ColumnFactory._initialized)
            ColumnFactory.init();
        ColumnFactory._columns.set(columnName, controlClass);
    }

    public static get(controlName: string): typeof Column {
        if (!ColumnFactory._initialized)
            ColumnFactory.init();
        return ColumnFactory._columns.get(controlName);
    }

    /**
     * Inicializace konkretniho objektu z factory
     * @param definition Definice sloupce
     * @param dataType Datový typ sloupce
     * @param attrInfo IPublicAttributeInfo
     * @param additionalParameters Dodatečné parametry pro vytvoření sloupce
     * @param disableSort Zakázání třídění
     * @param isCompact Zda je seznam v kompaktním režimu
     */
    public static initColumn(definition: IColumnDefinition, dataType: OWDataType | string, attrInfo?: IPublicAttributeInfo, additionalParameters?: IColumnParameters, disableSort?: boolean, isCompact: boolean = false): Column { // TODO: pro params udělat iface
        ColumnFactory.init();

        const columConstructor = definition.isIcon && ColumnFactory.get(OWDataType.icon) || ColumnFactory.get(definition.dataType || dataType) || ColumnFactory.get(dataType);
        let column: Column;

        if (columConstructor) {
            column = new columConstructor;
        } else {
            column = new Column();
        }

        column.headerTemplate.isCompact = isCompact;
        column.dataType = dataType;
        column.fullDataType = (attrInfo && attrInfo.additionalTypeName && attrInfo.type + "|" + attrInfo.additionalTypeName) || dataType;
        if (attrInfo) {
            column.allowGrouping = attrInfo.canGroup;
            column.displayAttributeName = attrInfo.displayAttributeName;
            column.canFilterNull = attrInfo.canFilterNull;
            column.canFilterCompare = attrInfo.canFilterCompare;
            column.canFilterEqual = attrInfo.canFilterEqual;
            column.canFilterLike = attrInfo.canFilterLike;
            column.canSort = attrInfo.canSort;
        }

        if (disableSort || !column.canSort)
            column.disableSort();

        column.initialize(definition, additionalParameters);

        return column;
    }
}

/**
 * Základní interface s doplňkovými parametry pro sloupce
 */
export interface IColumnParameters {
    [name: string]: any;
}

/**
 * Obsahuje informace o jednom sloupci seznamu
 * @includeToDoc
 */
export interface IColumnDefinition {
    // ! Musí být zachován formát názvu z atributu bez prefixu "data-" neb jsou použity pro jejich opětovné vytvoření na sloupcích

    /** Zobrazovaný atribut */
    attrName: string;
    /** Typ atributu */
    dataType?: OWDataType | string;
    /** ID atributu z definice (může být potřeba pro získání atributu s právy) */
    id?: string;
    /** Příznak, zda lze atribut editovat v editovatelném seznamu */
    isEditable?: boolean;
    /** Přepínač viditelnosti */
    visible?: boolean;
    /** Lokalizační klíč s nadpisem sloupce */
    locKey?: string;
    /** Parametry, které se vloží do lokalizovaného textu nadpisu sloupce */
    locParams?: string[];
    /** Požadovaná šířka sloupce */
    width?: number;
    /** Formátovací řetězec pro zobrazení hodnot čísel, datumů, bool hodnot a dalších */
    displayFormat?: string;
    /** Seznam atributů (sloupce), podle kterých jsou data implicitně setříděna */
    orderBy?: string;
    /** Nastavená kultura, např. pro potřeby formátování čísel */
    culture?: string;
    /** Atributy zobrazované v buňce mimo výchozí attrName */
    showAttrs?: string[];
    /** Umístění atributu do oblasti v kompaktním zobrazení seznamu */
    compactArea?: string;
    /** Pořadí atributu v dané oblasti v kompaktním zobrazení seznamu */
    compactAreaOrder?: number;
    /** Maska pro filtr nahrávaných souborů, např *.txt|Image Files (*.bmp, *.jpg)|*.bmp */
    fileMask?: string;
    /** Kód formuláře pro výběr záznamů */
    editWindow?: string;
    /** Způsob výběru záznamu v LOV,lsDropDown - dropdown menu nebo lsModal - modální okno se seznamem */
    lovStyle?: string;
    /** Koncová třída výběru záznamu pro LOV */
    validationClassName?: string;
    /** Příznak, zda se má detailový formulář otevřít se schovaným seznamem. */
    showOnlyDetail?: boolean;
    /** Podmínka pro omezení výběru záznamů */
    condition?: string;
    /** Středníkem oddělený seznam atributů aktuálního záznamu, jenž se použijí jako parametry pro omezující podmínku zadanou v Condition */
    conditionParams?: string;
    /** Název výčtového typu */
    enumName?: string;
    /**zda povolit podpis souboru bez přepnutí do editace*/
    allowsignwithoutedit?: string;
    /** Povolení kontrapodpisu */
    allowCounterSignature?: string;
    /** Název atributu pro TextLOVEdit */
    showAttr?: string;
    /** Středníkem oddělený seznam atributů (TextLOVEdit) */
    additionalAttrs?: string;
    /** Středníkem oddělený seznam atributů jejichž hodnoty se plní do atributů uvedených v additionalAttrs (TextLOVEdit) */
    showAdditionalAttrs?: string;
    /** Příznak, zda se jedná o ikonu */
    isIcon?: boolean;
    /** Příznak, zda se má hodnota zobrazit na více řádků */
    multiline?: boolean;
    /** Minimální možná číselná hodnota */
    minValue?: number;
    /** Maximální možná číselná hodnota */
    maxValue?: number;
    /** Příznak, jestli se májí ořezávat načítaná data atributu. Výchozí true */
    trimValue?: boolean;
    /**
     * Definice práv k danému sloupci, resp. ke control v tomto sloupci -  přesně tak, jak je to uvedeno v XML definici.
     * Slouží primárně jako podklad pro `ComponentRightsExtractor.loadRightsForTypes`.
     *
     * K prosinci 2020 se používá pouze u FileColumn pro předání do FileEdit a FileCellElement.
     */
    originalRightsDefinition?: IItemRightsCollection;
}

/**
 * Speciální typ sloupce s informacemi pro kalendář
 * @includeToDoc
 */
export interface ICalendarColumnDefinition extends IColumnDefinition {
    // ! Musí být zachován formát názvu z atributu bez prefixu "data-" neb jsou použity pro jejich opětovné vytvoření na sloupcích

    objectName?: string;
    /** Název atributu, který obsahuje popisek pro kalendářové položky */
    captionfld: string;
    /** Název atributu, který určuje počáteční datum popřípadě i čas události */
    dateFromfld: string;
    /** Název atributu, který určuje koncové datum popřípadě i čas události */
    dateTofld: string;
    /** Barva položky kaledáře z datového typu Flag */
    itemColor?: string;
    /** Příznak, zda se má zobrazovat také čas */
    includeTime?: boolean;
    /** Kod formuláře, který se otevře pro zobrazení detailu položky */
    showFormId: string;
    /** Zda je možné přidat novou položku */
    canInsert?: boolean;
    /** Seznam atributů, které kalendářová položka zobrazuje */
    textFields: string[];
}

/**
 * Speciální typ sloupce s informacemi pro graf
 * @includeToDoc
 * */
export interface IChartColumnDefinition extends IColumnDefinition {
    // !Musí být zachován formát názvu z atributu bez prefixu "data-" neb jsou použity pro jejich opětovné vytvoření na sloupcích

    /**
     * Název agregační případné agragační funkce pro danou hodnotu v grafu. Možní jsou hodnoty COUNT, AVG, MAX, MIN, SUM. Pozor: rozhoduje velikost písmen.
     */
    aggregateFunction?: AggregationFunction;
    /** Podmínka pro omezení výběru záznamů. */
    condition?: string;
    /** Výplň spojnicového grafu */
    fill?: boolean;
    /** Barva sloupce v grafu. */
    chartColor?: string;
    /** Typ grafu z výčtu */
    chartType?: ChartType;
    /** Zobrazení názvů os grafu */
    showAxisName?: boolean;
    /** Zobrazení hodnot na osách grafu */
    showAxisValue?: boolean;
    /** Zobrazení hodnot v grafu */
    showChartValue?: boolean;
    /** Lokalizovaný popisek ovládacího prvku. */
    caption?: string;
}

/**
 * Interface s parametry pro LOVColumn
 */
export interface ILovColumnParameters extends IColumnParameters {
    readVersioned: boolean;
    defaultWhisperer: string;
}

export class ILovColumnParameters {
    private static _properties = ["readVersioned", "defaultWhisperer"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}
